import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { NAV } from "@/constants/testIds";
import { CodiraBrand } from "@/components/Brand";
import {
  LayoutDashboard, Megaphone, LayoutTemplate, Film, Library,
  Share2, BarChart3, SlidersHorizontal, LogOut, Plus,
} from "lucide-react";

const items = [
  { to: "/",            label: "Dashboard",   icon: LayoutDashboard, tid: NAV.dashboard, end: true },
  { to: "/campaigns",   label: "Campaigns",   icon: Megaphone,        tid: NAV.campaigns },
  { to: "/templates",   label: "Templates",   icon: LayoutTemplate,   tid: NAV.templates },
  { to: "/renders",     label: "Renders",     icon: Film,             tid: NAV.renders },
  { to: "/assets",      label: "Assets",      icon: Library,          tid: NAV.assets },
  { to: "/social",      label: "Channels",    icon: Share2,           tid: NAV.social },
  { to: "/performance", label: "Performance", icon: BarChart3,        tid: NAV.performance },
  { to: "/settings",    label: "Settings",    icon: SlidersHorizontal,tid: NAV.settings },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const initials = (user?.name || user?.email || "?")
    .split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-white border-r border-gray-200 sticky top-0 h-screen flex flex-col">
        <div className="px-5 py-5 border-b border-gray-100">
          <NavLink to="/" data-testid={NAV.logo} className="flex items-center">
            <CodiraBrand className="h-7" />
          </NavLink>
          <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-2">
            Ad Studio · v1.0
          </p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 pl-3 pr-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? "bg-brand-50 text-brand-600"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`
                }
              >
                <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                <span>{it.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-gray-100 p-3 space-y-3">
          <button
            data-testid={NAV.newCampaignBtn}
            onClick={() => nav("/campaigns/new")}
            className="btn-brand w-full px-3 py-2.5 text-xs flex items-center justify-center gap-2"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
            New Campaign
          </button>

          <div className="flex items-center gap-2.5 px-1 pt-1">
            <div className="h-8 w-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-semibold shrink-0">
              {initials || "•"}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-gray-900 truncate">{user?.name || "—"}</p>
              <p className="text-[10px] text-gray-500 truncate">{user?.email}</p>
            </div>
            <button
              data-testid={NAV.logoutBtn}
              onClick={logout}
              className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="animate-fade-in">{children}</div>
      </main>
    </div>
  );
}
